'use client';

import { c as _c } from "react/compiler-runtime";
import type { ChainId } from '@morpho-org/blue-sdk';
import { DataList } from '@morpho-org/ui-v6/client';
import type { VaultV1Risk } from '@/lib/procedures/vaultV1Risk.contract';
import { AddressDataLine } from '@/components/common/DataLine';
import { getSafeQuorum, hasAragonMetadata } from '@/helpers/quorum';
import { VaultTimelockedFunctionName } from '@/types/enums';
import type { GuardianMetadataItems, PendingTimelock } from '../VaultHeroPendingActions/types';
import { GuardianDataLine } from '../VaultRiskDisclosures/RiskDisclosure/components';
export interface VaultRolesClientProps {
  chainId: ChainId;
  owner: VaultV1Risk['owner'];
  curator: VaultV1Risk['curator'];
  guardian: VaultV1Risk['guardian'];
  /** The vault's pending actions; empty when they could not be read. */
  pendingTimelocks: PendingTimelock[];
  currentGuardianMetadata: GuardianMetadataItems | undefined;
}
function VaultRolesClient(t0) {
  const $ = _c(37);
  const {
    chainId,
    owner,
    curator,
    guardian,
    pendingTimelocks,
    currentGuardianMetadata
  } = t0;
  let t1;
  if ($[0] !== pendingTimelocks) {
    t1 = pendingTimelocks.filter(_temp);
    $[0] = pendingTimelocks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const pendingSetGuardianTimelocks = t1;
  let t2;
  if ($[2] !== owner.metadata) {
    t2 = getSafeQuorum(owner.metadata);
    $[2] = owner.metadata;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const ownerSafeQuorum = t2;
  let t3;
  if ($[4] !== curator) {
    t3 = curator ? getSafeQuorum(curator.metadata) : undefined;
    $[4] = curator;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const curatorSafeQuorum = t3;
  let t4;
  if ($[6] !== guardian) {
    t4 = guardian ? getSafeQuorum(guardian.metadata) : undefined;
    $[6] = guardian;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const guardianSafeQuorum = t4;
  let t5;
  if ($[8] !== owner.metadata) {
    t5 = hasAragonMetadata(owner.metadata);
    $[8] = owner.metadata;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const isOwnerAragonDAO = t5;
  let t6;
  if ($[10] !== curator) {
    t6 = curator ? hasAragonMetadata(curator.metadata) : false;
    $[10] = curator;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const isCuratorAragonDAO = t6;
  let t7;
  if ($[12] !== guardian) {
    t7 = guardian ? hasAragonMetadata(guardian.metadata) : false;
    $[12] = guardian;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const isGuardianAragonDAO = t7;
  let t8;
  if ($[14] !== chainId || $[15] !== isOwnerAragonDAO || $[16] !== owner.address || $[17] !== ownerSafeQuorum) {
    t8 = <AddressDataLine title="Owner" chainId={chainId} toastMessage="Owner address copied to clipboard." tooltipMessage="Copy owner address." address={owner.address} safeQuorum={ownerSafeQuorum} isAragonDAO={isOwnerAragonDAO} roleTooltip="Assigns and changes the key roles that govern the vault. Has no direct control over funds or risk parameters. Only one address can hold this role." />;
    $[14] = chainId;
    $[15] = isOwnerAragonDAO;
    $[16] = owner.address;
    $[17] = ownerSafeQuorum;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const t9 = curator?.address;
  let t10;
  if ($[19] !== chainId || $[20] !== curatorSafeQuorum || $[21] !== isCuratorAragonDAO || $[22] !== t9) {
    t10 = <AddressDataLine title="Curator" chainId={chainId} toastMessage="Curator address copied to clipboard." tooltipMessage="Copy curator address." address={t9} safeQuorum={curatorSafeQuorum} isAragonDAO={isCuratorAragonDAO} roleTooltip="Configures risk parameters and allocation rules of the vault. Only one address can hold this role." />;
    $[19] = chainId;
    $[20] = curatorSafeQuorum;
    $[21] = isCuratorAragonDAO;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t8) {
    t11 = <DataList.Column>{t8}{t10}</DataList.Column>;
    $[24] = t10;
    $[25] = t8;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  const t12 = guardian?.address;
  let t13;
  if ($[27] !== chainId || $[28] !== currentGuardianMetadata || $[29] !== guardianSafeQuorum || $[30] !== isGuardianAragonDAO || $[31] !== pendingSetGuardianTimelocks || $[32] !== t12) {
    t13 = <DataList.Column><GuardianDataLine chainId={chainId} pendingTimelocks={pendingSetGuardianTimelocks} address={t12} safeQuorum={guardianSafeQuorum} isAragonDAO={isGuardianAragonDAO} currentGuardianMetadata={currentGuardianMetadata} /></DataList.Column>;
    $[27] = chainId;
    $[28] = currentGuardianMetadata;
    $[29] = guardianSafeQuorum;
    $[30] = isGuardianAragonDAO;
    $[31] = pendingSetGuardianTimelocks;
    $[32] = t12;
    $[33] = t13;
  } else {
    t13 = $[33];
  }
  let t14;
  if ($[34] !== t11 || $[35] !== t13) {
    t14 = <DataList.Root fullWidth={true}>{t11}{t13}</DataList.Root>;
    $[34] = t11;
    $[35] = t13;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  return t14;
}
function _temp(item) {
  return item.functionName === VaultTimelockedFunctionName.SetGuardian;
}
export default VaultRolesClient;